import { Controller } from "@hotwired/stimulus";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { addMapBackground } from "../utils/map_background";

// Fix Leaflet default icon paths (broken by bundlers)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const STATUS_COLORS = {
  active: "#47A534",
  attention: "#F5A623",
  inactive: "#E1463E",
};

export default class extends Controller {
  static targets = [
    "map",
    "data",
    "listItem",
    "statusFilter",
    "cycleFilter",
    "panelList",
    "visibleCount",
  ];

  static values = {
    activeLabel: { type: String, default: "Actif" },
    watchLabel: { type: String, default: "\u00C0 surveiller" },
    inactiveLabel: { type: String, default: "Inactif" },
    successRateLabel: { type: String, default: "r\u00E9ussite" },
    viewDetailsLabel: {
      type: String,
      default: "Voir la fiche compl\u00E8te",
    },
    studentsLabel: { type: String, default: "\u00E9l\u00E8ves" },
  };

  connect() {
    if (!this.hasDataTarget) return;
    this.allMarkers = JSON.parse(this.dataTarget.textContent);
    if (!this.allMarkers || this.allMarkers.length === 0) return;

    this.markerMap = {};
    this.populateCycleFilter();
    this.initMap(this.allMarkers);
  }

  disconnect() {
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = null;
    }
    if (this._popupTimeout) {
      clearTimeout(this._popupTimeout);
      this._popupTimeout = null;
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  initMap(markers) {
    this.map = L.map(this.mapTarget, {
      zoomControl: false,
      scrollWheelZoom: true,
    });

    L.control.zoom({ position: "bottomright" }).addTo(this.map);

    // CARTO now answers « API key required »: the OpenFreeMap background.
    addMapBackground(this.map);

    this.clusterGroup = L.markerClusterGroup({
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
    });

    markers.forEach((m) => {
      const marker = L.marker([m.lat, m.lng], {
        icon: this.buildIcon(m.status),
      });
      marker.schoolData = m;
      marker.bindPopup(this.buildPopup(m), {
        maxWidth: 300,
        minWidth: 260,
        className: "g4sp-map-popup-wrapper",
      });

      marker.on("popupopen", () => this.highlightListItem(m.id));
      marker.on("popupclose", () => this.clearListHighlight());

      this.clusterGroup.addLayer(marker);
      this.markerMap[m.id] = marker;
    });

    this.map.addLayer(this.clusterGroup);

    if (markers.length === 1) {
      this.map.setView([markers[0].lat, markers[0].lng], 14);
    } else {
      const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
      this.map.fitBounds(bounds, { padding: [40, 40] });
    }

    this._resizeTimeout = setTimeout(
      () => this.map.invalidateSize(),
      200
    );
  }

  buildIcon(status) {
    const color = STATUS_COLORS[status] || STATUS_COLORS.active;
    return L.divIcon({
      className: "g4sp-marker",
      html: `<div class="g4sp-marker-pin" style="background:${color};"></div>`,
      iconSize: [24, 34],
      iconAnchor: [12, 34],
      popupAnchor: [0, -36],
    });
  }

  buildPopup(m) {
    const initials = m.name
      .split(/[\s-]+/)
      .filter((w) => w.length > 0 && w[0] === w[0].toUpperCase())
      .slice(0, 3)
      .map((w) => w.charAt(0))
      .join("")
      .toUpperCase();

    const statusColor = STATUS_COLORS[m.status] || STATUS_COLORS.active;
    const statusLabel =
      m.status === "active"
        ? this.activeLabelValue
        : m.status === "attention"
        ? this.watchLabelValue
        : this.inactiveLabelValue;

    // Location: "City — Zone"
    const locationParts = [m.city, m.zone].filter(Boolean);
    const locationText = locationParts.join(" \u2014 ");

    const directorHtml = m.director_name
      ? `<div class="g4sp-popup-info-row">
           <span class="g4sp-popup-info-icon">\uD83D\uDC64</span>
           <span>${m.director_name}</span>
         </div>`
      : "";

    const phoneHtml = m.phone
      ? `<div class="g4sp-popup-info-row">
           <span class="g4sp-popup-info-icon">\uD83D\uDCDE</span>
           <span>${m.phone}</span>
         </div>`
      : "";

    const successHtml =
      m.success_rate != null
        ? `<span class="g4sp-popup-stat-inline">
             <strong>${m.success_rate}%</strong> ${this.successRateLabelValue}
           </span>`
        : "";

    const ctaLabel = this.viewDetailsLabelValue;

    return `
      <div class="g4sp-map-popup">
        <div class="g4sp-popup-header">
          <div class="g4sp-popup-avatar">${initials}</div>
          <div class="g4sp-popup-title-block">
            <span class="g4sp-popup-name">${m.name}</span>
            <span class="g4sp-popup-meta">${[m.code, m.cycle].filter(Boolean).join(" \u00B7 ")}</span>
          </div>
        </div>

        <div class="g4sp-popup-details">
          <div class="g4sp-popup-info-row">
            <span class="g4sp-popup-info-icon">\uD83D\uDCCD</span>
            <span>${locationText}</span>
          </div>
          ${directorHtml}
          ${phoneHtml}
        </div>

        <div class="g4sp-popup-stats-row">
          <span class="g4sp-popup-stat-inline">
            <strong>${m.students_count}</strong> ${this.studentsLabelValue}
          </span>
          ${successHtml}
        </div>

        <div class="g4sp-popup-badge-row">
          <span class="g4sp-popup-badge" style="color: ${statusColor};">
            <span class="g4sp-popup-badge-dot" style="background: ${statusColor};"></span>
            ${statusLabel}
          </span>
        </div>

        <a href="${m.detail_url}"
           class="g4sp-popup-cta"
           data-turbo="true">
          ${ctaLabel} \u2192
        </a>
      </div>
    `;
  }

  // ── Panel interaction ──

  selectSchool(event) {
    const btn = event.currentTarget;
    const schoolId = parseInt(btn.dataset.schoolId, 10);
    const marker = this.markerMap[schoolId];
    if (!marker) return;

    this.clusterGroup.zoomToShowLayer(marker, () => {
      this.map.flyTo(marker.getLatLng(), 15, { duration: 0.8 });
      this._popupTimeout = setTimeout(
        () => marker.openPopup(),
        300
      );
    });
  }

  highlightListItem(schoolId) {
    this.clearListHighlight();
    if (!this.hasListItemTarget) return;

    this.listItemTargets.forEach((el) => {
      if (parseInt(el.dataset.schoolId, 10) === schoolId) {
        el.classList.add("selected");
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    });
  }

  clearListHighlight() {
    if (!this.hasListItemTarget) return;
    this.listItemTargets.forEach((el) => el.classList.remove("selected"));
  }

  // ── Filtering ──

  populateCycleFilter() {
    if (!this.hasCycleFilterTarget) return;
    const cycles = [
      ...new Set(
        this.allMarkers.map((m) => m.cycle).filter(Boolean)
      ),
    ].sort();

    cycles.forEach((c) => {
      const opt = document.createElement("option");
      opt.value = c;
      opt.textContent = c;
      this.cycleFilterTarget.appendChild(opt);
    });
  }

  applyFilters() {
    const statusVal = this.hasStatusFilterTarget
      ? this.statusFilterTarget.value
      : "";
    const cycleVal = this.hasCycleFilterTarget
      ? this.cycleFilterTarget.value
      : "";

    let visibleCount = 0;

    this.allMarkers.forEach((m) => {
      const marker = this.markerMap[m.id];
      if (!marker) return;

      const matchStatus = !statusVal || m.status === statusVal;
      const matchCycle = !cycleVal || (m.cycle && m.cycle.includes(cycleVal));
      const visible = matchStatus && matchCycle;

      if (visible) {
        if (!this.clusterGroup.hasLayer(marker)) {
          this.clusterGroup.addLayer(marker);
        }
        visibleCount++;
      } else {
        this.clusterGroup.removeLayer(marker);
      }
    });

    // Update panel list visibility
    if (this.hasListItemTarget) {
      this.listItemTargets.forEach((el) => {
        const elStatus = el.dataset.schoolStatus;
        const elCycle = el.dataset.schoolCycle || "";
        const matchS = !statusVal || elStatus === statusVal;
        const matchC = !cycleVal || elCycle.includes(cycleVal);
        el.style.display = matchS && matchC ? "" : "none";
      });
    }

    // Update visible count badge
    if (this.hasVisibleCountTarget) {
      this.visibleCountTarget.textContent = visibleCount;
    }
  }
}
